import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ClientRecord, Item, clientsApi, formatMoney } from "@/lib/catalog-api";
import { ItemCard } from "@/components/catalog/ItemCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/catalog/Skeleton";

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<ClientRecord | null>(null);
  const [items, setItems] = useState<Item[] | null>(null);

  useEffect(() => {
    if (!id) return;
    clientsApi.get(id).then(setClient).catch(() => setClient(null));
    clientsApi.items(id).then((r) => setItems(r.items as any)).catch(() => setItems([]));
  }, [id]);

  if (!client) return <main className="flex-1 px-6 md:px-10 py-10"><Skeleton className="h-10 w-1/3" /></main>;

  const totalValue = (items || []).reduce((sum, it) => sum + (it.insured_value || it.retail_value || 0), 0);

  async function archive() {
    if (!window.confirm(`Archive ${client?.name}?`)) return;
    if (!id) return;
    await clientsApi.archive(id);
    navigate("/app/clients");
  }

  return (
    <main className="flex-1 px-6 md:px-10 py-10" data-testid="client-detail-page">
      <Link to="/app/clients" className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-oxblood inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-3 h-3" /> All clients
      </Link>
      <h1 className="font-display text-3xl sm:text-4xl" data-testid="client-detail-name">{client.name}</h1>
      {client.pronouns && <p className="text-sm text-muted-foreground mt-1">{client.pronouns}</p>}

      <div className="mt-6 grid sm:grid-cols-3 gap-px bg-border border border-border max-w-2xl">
        <div className="bg-background p-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Items</p>
          <p className="font-display text-2xl mt-1" data-testid="client-stat-items">{(items || []).length}</p>
        </div>
        <div className="bg-background p-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Insured value</p>
          <p className="font-display text-2xl mt-1" data-testid="client-stat-value">{formatMoney(totalValue)}</p>
        </div>
        <div className="bg-background p-4">
          <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Brands</p>
          <p className="font-display text-2xl mt-1">{client.preferred_brands.length}</p>
        </div>
      </div>

      {client.preferred_brands.length > 0 && (
        <div className="mt-6">
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Preferred brands</p>
          <div className="flex flex-wrap gap-1.5">
            {client.preferred_brands.map((b) => <span key={b} className="text-xs px-2 py-1 border border-border">{b}</span>)}
          </div>
        </div>
      )}

      {client.notes && (
        <div className="mt-6 max-w-xl">
          <p className="text-[11px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Notes</p>
          <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">{client.notes}</p>
        </div>
      )}

      <div className="mt-12">
        <h2 className="font-display text-2xl mb-5">Assigned items</h2>
        {items === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items assigned to this client.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((it, i) => <ItemCard key={it._id} item={it} index={i} />)}
          </div>
        )}
      </div>

      {!client.archived && (
        <div className="mt-12 pt-6 border-t border-border">
          <Button onClick={archive} data-testid="client-archive-btn"
            className="bg-background hover:bg-oxblood/5 border border-oxblood/40 text-oxblood rounded-none h-9 px-4 text-xs uppercase tracking-[0.18em]">
            Archive client
          </Button>
        </div>
      )}
    </main>
  );
}
